import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { InvoiceStatusPill, fmtMoney, fmtDate, fmtRelative } from "@/lib/workflow";

export default function InvoicesList({ portal = "clinic" }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();

  const load = () => {
    setLoading(true);
    api.get("/invoices")
      .then((r) => setItems(r.data || []))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const linkBase = portal === "vendor" ? "/vendor/workflows" : "/portal/workflows";
  const total = items.reduce((s, i) => s + (Number(i.amount_due) || 0), 0);

  return (
    <div className="space-y-5" data-testid="invoices-list">
      <div>
        <div className="text-[11px] uppercase tracking-[0.18em] font-semibold text-slate-500">{portal} · Invoices</div>
        <h1 className="font-display text-2xl font-bold text-slate-900">Invoices</h1>
        <p className="text-sm text-slate-500 mt-1 max-w-2xl">
          {portal === "vendor" ? "Invoices you've submitted to clinics. Track approval and payment statuses." : "Invoices submitted to your clinic by vendor partners."}
        </p>
      </div>
      <div className="grid grid-cols-3 gap-3 max-w-md">
        <Stat label="Total billed" value={fmtMoney(total)} />
        <Stat label="Open" value={items.filter((i) => i.status !== "paid").length} />
        <Stat label="Paid" value={items.filter((i) => i.status === "paid").length} />
      </div>
      {loading ? (
        <div className="text-sm text-slate-500">Loading…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-slate-300 bg-white p-10 text-center text-slate-500">
          No invoices yet.
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-200 bg-white overflow-x-auto" data-testid="invoices-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-[11px] uppercase tracking-wider text-slate-500">
              <tr>
                <th className="text-left px-4 py-3">Invoice #</th>
                <th className="text-left px-4 py-3">Order</th>
                <th className="text-left px-4 py-3">{portal === "vendor" ? "Clinic" : "Vendor"}</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-left px-4 py-3">Due</th>
                <th className="text-left px-4 py-3">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {items.map((inv) => (
                <tr key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                  <td className="px-4 py-3 font-mono text-xs font-semibold">{inv.invoice_number}</td>
                  <td className="px-4 py-3">
                    <Link to={`${linkBase}/${inv.workflow_request_id}`} className="text-blue-700 hover:text-blue-900 font-mono text-xs">
                      {inv.order_id}
                    </Link>
                    <div className="text-[11px] text-slate-400">{fmtRelative(inv.created_at)}</div>
                  </td>
                  <td className="px-4 py-3">{portal === "vendor" ? inv.clinic_name : inv.vendor_name}</td>
                  <td className="px-4 py-3 text-right font-semibold">{fmtMoney(inv.amount_due, inv.currency)}</td>
                  <td className="px-4 py-3 text-xs text-slate-600">{fmtDate(inv.due_date)}</td>
                  <td className="px-4 py-3"><InvoiceStatusPill status={inv.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="text-[10px] text-slate-400 max-w-2xl">
        Nodera enables operational payment workflows through Stripe and does not custody or transmit funds. Phase 2 will route "Render payment" actions through Stripe Connect.
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-3 py-2.5">
      <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-slate-400">{label}</div>
      <div className="font-display text-base font-bold text-slate-900">{value}</div>
    </div>
  );
}
